import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type InviteMemberResult = {
  emailSent: boolean;
  deliveryMessage?: string;
};

type OrganizationInviteMemberDialogProps = {
  open: boolean;
  organizationName?: string;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteMember: (email: string) => Promise<InviteMemberResult>;
};

function normalizeEmail(value: string) {
  return value.trim().toLowerCase();
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function OrganizationInviteMemberDialog({
  open,
  organizationName,
  isSubmitting,
  onOpenChange,
  onInviteMember,
}: OrganizationInviteMemberDialogProps) {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const normalizedEmail = useMemo(() => normalizeEmail(email), [email]);
  const canSubmit = isValidEmail(normalizedEmail) && !isSubmitting;

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);

    if (!nextOpen) {
      setEmail('');
      setErrorMessage(null);
    }
  };

  const handleSubmit = async () => {
    if (!normalizedEmail) {
      setErrorMessage('Email is required.');
      return;
    }

    if (!isValidEmail(normalizedEmail)) {
      setErrorMessage('Enter a valid email address.');
      return;
    }

    setErrorMessage(null);

    try {
      await onInviteMember(normalizedEmail);
      setEmail('');
      handleOpenChange(false);
    } catch (err) {
      const error = err as { message?: string };
      setErrorMessage(error?.message || 'Failed to invite member.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
          <DialogDescription>
            Send a business invitation{organizationName ? ` to join ${organizationName}` : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="member-invite-email">Member Email</Label>
            <Input
              id="member-invite-email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (errorMessage) setErrorMessage(null);
              }}
              placeholder="name@company.com"
              autoComplete="email"
              autoFocus
            />
          </div>

          <ErrorNotice message={errorMessage} title="Invitation Failed" />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
